import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/apiClient';
import CommonConfig from '../../utils/constant';
import APIConstant from '../../utils/pathConstants';
import { apiBase } from '../../utils/config';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Button } from 'primereact/button';
import Header from '../Header/Header';
import { createPortal } from "react-dom";
import Toast from '../Shared/Toast/Toast';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import Loader from '../Shared/Loader/Loader';
import AccountStore from '../../utils/accountStore';
import { useCatchResponse } from '../../utils/useCatchResponse';
import useSocket from '../../utils/useSocket';
import ThemePicker from '../Theme/ThemePicker';
import EduTattvaMono from '../../assets/images/EduTattvaMono.svg';
import portlinksLogo from '../../assets/images/portlinkLogo.svg';


const Sidebar = (props) => {
  const { catchResponse } = useCatchResponse();
  const [sidebarMenus, setSidebarMenus] = useState([]);
  const [menuSearch, setMenuSearch] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const [userRole, setUserRole] = useState('');
  const [selectedChildName, setSelectedChildName] = useState('');
  const [selectedChildProfile, setSelectedChildProfile] = useState('');
  const [childAcademicDataList, setChildAcademicDataList] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationType, setNotificationType] = useState('ALL');
  const [securityUserID, setSecurityUserID] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  // Notification access (from the "Notifications" menu). No View access hides the
  // bell + list + page entirely; Delete access gates the clear/delete action.
  const notifAccess = CommonConfig.getUserAccess('Notifications') || CommonConfig.getUserAccess('Notification') || {};
  const canViewNotif = !!notifAccess?.ViewAccess;
  const canDeleteNotif = !!notifAccess?.DeleteAccess;
  const popupRef = useRef(null);
  const themePickerRef = useRef(null);
  const sidebarRef = useRef(null);
  const [logoWhite, setLogoWhite] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  // Whiten the PortLinks footer logo when the sidebar has a dark background
  // (dark theme); keep the default coloured logo on light backgrounds.
  useEffect(() => {
    const measure = () => {
      const el = sidebarRef.current;
      if (!el) return;
      const style = window.getComputedStyle(el);
      // Prefer a solid background-color; fall back to the first colour stop of
      // a gradient background-image (the navy skin uses a gradient).
      let parts = (style.backgroundColor || '').match(/[\d.]+/g);
      const opaque = parts && parts.length >= 3 && Number(parts[3] ?? 1) !== 0;
      if (!opaque) {
        const rgbInImage = (style.backgroundImage || '').match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/);
        if (rgbInImage) {
          parts = [rgbInImage[1], rgbInImage[2], rgbInImage[3]];
        } else {
          return; // nothing measurable — leave logo as default
        }
      }
      const [r, g, b] = parts;
      const luminance = (0.299 * Number(r) + 0.587 * Number(g) + 0.114 * Number(b)) / 255;
      setLogoWhite(luminance < 0.5);
    };
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  const notificationTypeOptions = [
    { label: "All", value: "ALL" },
    { label: "General", value: "GENERAL" },
    { label: "Attendance", value: "ATTENDANCE" },
    { label: "Fees", value: "FEES" },
    { label: "Exam", value: "EXAM" },
  ];
  const [notifications, setNotifications] = useState([]);
  const getInitialPosition = () => {
    // Vertically centered on the school card, docked at its right edge.
    const card = typeof document !== 'undefined'
      ? document.querySelector('.sidebar .user')
      : null;
    if (card) {
      const r = card.getBoundingClientRect();
      return { x: Math.round(r.right - 19), y: Math.round(r.top + r.height / 2 - 19) };
    }
    return { x: 261, y: 68 };
  };

  const savedPosition = localStorage.getItem("bellPosition");
  const [position, setPosition] = useState(
    savedPosition ? JSON.parse(savedPosition) : getInitialPosition()
  );

  const targetPosition = useRef({ x: position.x, y: position.y });
  const [isDragging, setIsDragging] = useState(false);
  const animationRef = useRef(null);
  const returnTimeoutRef = useRef(null);

  const filteredNotifications =
    notificationType && notificationType !== "ALL"
      ? notifications.filter((n) => n.type === notificationType)
      : notifications;

  const groupedNotifications = {
    Today: [],
    Yesterday: [],
    Older: [],
  };

  useSocket(null, (newNoti) => {
    if (CommonConfig.getCookie('loggedIn')) {
      const loginData = CommonConfig.loginData("ERPLoginData");
      const userId = loginData?._id;

      if (!userId) return;

      const yearId =
        loginData?.UserRole === "Parent"
          ? loginData?.SelectedAcademicYearList?.[loginData?.SelectedChildData?._id]
          : selectedYear;
      GetNotificationList(userId, yearId ?? "", notificationType || "ALL", 1, limit, false, '');
    }
  });

  useEffect(() => {
    const savedPos = localStorage.getItem("bellPosition");
    if (savedPos) {
      const pos = JSON.parse(savedPos);
      targetPosition.current = pos;
      setPosition(pos);
    }
    const loginData = CommonConfig.loginData();
    setUserRole(loginData?.UserRole);
    if (loginData?.UserRole === 'Parent') {
      const childName = loginData?.SelectedChildData?.Name ?? '';
      const childProfile = loginData?.SelectedChildData?.ProfilePhotoUrl ?? '';
      setSelectedChildName(childName);
      setSelectedChildProfile(childProfile);
      setSelectedYear(loginData?.SelectedAcademicYearList?.[loginData?.SelectedChildData?._id] ?? '');
      setChildAcademicDataList(loginData?.SelectedChildData?.AcademicDataList ?? '');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const savedPos = localStorage.getItem("bellPosition");
        if (savedPos) {
          const pos = JSON.parse(savedPos);
          targetPosition.current = pos;
          setPosition(pos);
        }
      }
    };

    const handleResize = () => {
      const savedPos = localStorage.getItem("bellPosition");
      if (!savedPos) {
        const newPosition = getInitialPosition();
        targetPosition.current = newPosition;
        setPosition(newPosition);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    animate();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (returnTimeoutRef.current) clearTimeout(returnTimeoutRef.current);
    };
  }, []);

  // Keep the notification bell vertically centered on the school card's right edge —
  // responsive to card size/content and screen size; also recenters on navigation.
  useEffect(() => {
    const centerOnCard = () => {
      const card = document.querySelector('.sidebar .user');
      if (!card) return;
      const r = card.getBoundingClientRect();
      const pos = {
        x: Math.round(r.right - 19),
        y: Math.round(r.top + r.height / 2 - 19),
      };
      targetPosition.current = pos;
      setPosition(pos);
    };
    centerOnCard();
    const card = document.querySelector('.sidebar .user');
    let ro;
    if (card && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(centerOnCard);
      ro.observe(card);
    }
    window.addEventListener('resize', centerOnCard);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', centerOnCard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    setShowNotifications(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    setSecurityUserID(loginData?._id || '');
    setUserRole(loginData?.UserRole);
    if (loginData?.UserRole === 'Parent') {
      const childName = loginData?.SelectedChildData?.Name ?? '';
      const childProfile = loginData?.SelectedChildData?.ProfilePhotoUrl ?? '';
      setSelectedChildName(childName);
      setSelectedChildProfile(childProfile);
      setSelectedYear(loginData?.SelectedAcademicYearList?.[loginData?.SelectedChildData?._id] ?? '');
      setChildAcademicDataList(loginData?.SelectedChildData?.AcademicDataList ?? '');
      GetNotificationList(loginData?._id, loginData?.SelectedAcademicYearList?.[loginData?.SelectedChildData?._id] ?? '', notificationType || "ALL", 1, limit, false, '');
    } else {
      getFinancialYearData(loginData);
    }
    getUserMenu();
  }, [])

  const getFinancialYearData = async (userData) => {
    const years = await CommonConfig.getFinancialYears(CommonConfig.isOrgOwner(userData?.UserRole) ? userData?._id : userData?.CreatedBy);
    const currentYear = CommonConfig.getCurrentFinancialYear(years);
    const selectedAcYear = CommonConfig.getCookie('selectedAcademicYear');
    setSelectedYear(selectedAcYear ?? currentYear?.value);
    await GetNotificationList(userData?._id, selectedAcYear ?? currentYear?.value, notificationType || "ALL", 1, limit, false, '');
  }

  useEffect(() => {
    const handleCustom = () => {
      const loginData = CommonConfig.loginData();
      setUserRole(loginData?.UserRole);
      if (loginData?.UserRole === 'Parent') {
        const childName = loginData?.SelectedChildData?.Name ?? '';
        const childProfile = loginData?.SelectedChildData?.ProfilePhotoUrl ?? '';
        setSelectedChildName(childName);
        setSelectedChildProfile(childProfile);
        setSelectedYear(loginData?.SelectedAcademicYearList?.[loginData?.SelectedChildData?._id] ?? '');
        setChildAcademicDataList(loginData?.SelectedChildData?.AcademicDataList ?? '');
      }
    };

    window.addEventListener('local-storage-change', handleCustom);

    return () => {
      window.removeEventListener('local-storage-change', handleCustom);
    };
  }, []);

  const isToday = (date) => {
    const d = new Date(date);
    const now = new Date();
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  };

  const isYesterday = (date) => {
    const d = new Date(date);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return (
      d.getDate() === yesterday.getDate() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getFullYear() === yesterday.getFullYear()
    );
  };

  filteredNotifications.forEach((n) => {
    if (isToday(n.createdAt)) groupedNotifications.Today.push(n);
    else if (isYesterday(n.createdAt)) groupedNotifications.Yesterday.push(n);
    else groupedNotifications.Older.push(n);
  });

  const animate = () => {
    setPosition((prev) => {
      const dx = targetPosition.current.x - prev.x;
      const dy = targetPosition.current.y - prev.y;
      const ease = 0.2;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return prev;
      return { x: prev.x + dx * ease, y: prev.y + dy * ease };
    });
    animationRef.current = requestAnimationFrame(animate);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);

    const startX = e.clientX - targetPosition.current.x;
    const startY = e.clientY - targetPosition.current.y;

    if (returnTimeoutRef.current) clearTimeout(returnTimeoutRef.current);

    const bellWidth = 60;
    const bellHeight = 60;

    const handleMouseMove = (moveEvent) => {
      let newX = moveEvent.clientX - startX;
      let newY = moveEvent.clientY - startY;

      // ✅ CONSTRAIN DURING DRAG
      newX = Math.max(0, Math.min(newX, window.innerWidth - bellWidth));
      newY = Math.max(0, Math.min(newY, window.innerHeight - bellHeight));

      targetPosition.current = {
        x: newX,
        y: newY
      };
    };

    const handleMouseUp = () => {
      setIsDragging(false);

      // ✅ SAVE POSITION
      localStorage.setItem("bellPosition", JSON.stringify(targetPosition.current));

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();

    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 10) return "Just now";
    if (diffSec < 60) return `${diffSec} sec ago`;
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHr < 24) return `${diffHr} hours ago`;
    if (diffDay === 1) return "Yesterday";
    return `${diffDay} days ago`;
  };

  const GetNotificationList = async (id, financialYearId, type, page, limit, append = false, schoolId) => {
    // No View access to Notifications → don't fetch or surface anything.
    if (!canViewNotif) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }
    try {
      setIsLoadingNotifications(true);
      let data = {
        UserId: id,
        AcademicYearId: financialYearId,
        Type: type || 'ALL',
        Page: page || 1,
        Limit: limit || 20,
        SchoolId: schoolId || (CommonConfig.isOrgOwner(CommonConfig.loginData()?.UserRole) ? CommonConfig.loginData()?._id : CommonConfig.loginData()?.CreatedBy)
      }
      await api.post(APIConstant.path.GetNotificationsList, data).then(async response => {
        let res = response;
        if (res.success) {
          setTotal(res.total || 0);
          setUnreadCount(res.unreadCount || 0);

          if (append) {
            setNotifications((prev) => [...prev, ...(res.data || [])]);
          } else {
            setNotifications(res.data || []);
          }
        } else {
          if (!append) setNotifications([]);
        }

        setIsLoadingNotifications(false);
      }).catch(err => {
        console.log('Eroor in get notification', err);
        setIsLoadingNotifications(false);
        catchResponse(err);
      });
    } catch (err) {
      console.log(err);
      catchResponse(err);
    }
  }

  const getUserMenu = async () => {
    try {
      const user = CommonConfig.loginData();
      let userId = user?._id;
      let userType = user?.UserType;
      if (user?.UserRole === 'Parent') {
        if (CommonConfig.isEmpty(user?.SelectedChildData)) {
          setSidebarMenus([]);
          return;
        }
        userId = !CommonConfig.isEmpty(user?.SelectedChildData) ? user?.SelectedChildData?._id : user?._id;
        userType = !CommonConfig.isEmpty(user?.SelectedChildData?.UserType) ? user?.SelectedChildData?.UserType : user?.UserType;
      }
      const data = { UserId: userId, RoleId: userType };
      const res = await api.post(APIConstant.path.getUserMenu, data);

      if (!res.success) {
        setSidebarMenus([]);
        return;
      }

      if (res.data.length) {
        const menus = transformMenuData(res.data);
                const finalMenus = await injectGpsAttendanceMenu(menus, user);
        setSidebarMenus(finalMenus);
        // Store allowed paths for route protection
        const allowedPaths = extractAllPaths(finalMenus);
        CommonConfig.setCookie('allowedPaths', JSON.stringify(allowedPaths));
      } else if (user?.UserRole === 'Parent') {
        setSidebarMenus([]);
        Toast.error({ message: 'No menu assigned for the selected child. Please contact administrator.' });
        navigate('/childreen-list');
      }
      else {
        setSidebarMenus([]);
        navigate('/edit-profile');
      }
    } catch (err) {
      console.error('Error fetching user menu:', err);
    }
  };

    const injectGpsAttendanceMenu = async (menus, user) => {
    try {
      if (user?.UserRole !== 'Teacher') return menus;
      const schoolId = user?.CreatedBy;
      if (!schoolId) return menus;
      const res = await api.post(APIConstant.path.GetGeneralConfigurationDetail, { id: schoolId });
      if (!res?.success || !res?.data?.IsGpsAttendanceEnabled) return menus;
      const alreadyPresent = menus.some(m => m.path === '/mark-my-attendance');
      if (alreadyPresent) return menus;
      return [
        ...menus,
        {
          label: 'Mark My Attendance',
          icon: 'pi pi-fw pi-map-marker',
          path: '/mark-my-attendance',
          isActive: false,
        },
      ];
    } catch (err) {
      return menus;
    }
  };

  const transformMenuData = (data) => {
    return data.map((menu) => {
      const transformedMenu = {
        id: menu?._id ?? menu?.id,
        label: menu.Title,
        icon: menu.Icon || 'pi pi-fw pi-info-circle',
        path: menu?.Path,
        isActive: false,
      };

      if (menu.Children && menu.Children.length > 0) {
        transformedMenu.items = menu.Children.map((child) => {
          const transformedChild = {
            id: child?._id ?? child?.id,
            label: child.Title,
            icon: child.Icon || 'pi pi-fw pi-folder',
            path: child?.Path,
            isActive: false
          };
          if (child.ChildSubMenus && child.ChildSubMenus.length > 0) {
            transformedChild.items = child.ChildSubMenus.map((subChild) => ({
              id: subChild?._id ?? subChild?.id,
              label: subChild.Title,
              icon: subChild.Icon || 'pi pi-fw pi-file',
              path: subChild?.Path,
              isActive: false
            }));
          }
          return transformedChild;
        });
      }
      return transformedMenu;
    });
  };

  const searchText = menuSearch.trim().toLowerCase();

  const filterMenuItem = (item, search) => {
    const selfMatches = (item.label || '').toLowerCase().includes(search);
    if (selfMatches) return item;
    const matchedChildren = (item.items || [])
      .map((child) => filterMenuItem(child, search))
      .filter(Boolean);
    if (matchedChildren.length) return { ...item, items: matchedChildren };
    return null;
  };

  const filteredMenus = useMemo(() => {
    if (!searchText) return sidebarMenus;
    return sidebarMenus.map((m) => filterMenuItem(m, searchText)).filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMenus, searchText]);

  const toggleMenuExpanded = (menu) => {
    setSidebarMenus((prev) =>
      prev.map((m) => (m.label === menu.label && m.path === menu.path ? { ...m, isActive: !m.isActive } : m))
    );
  };

  const extractAllPaths = (menus) => {
    const paths = [];
    const extract = (items) => {
      (items || []).forEach(item => {
        if (item.path) paths.push(item.path);
        if (item.items) extract(item.items);
      });
    };
    extract(menus);
    return paths;
  };

  const getMenuUserId = () => {
    const user = CommonConfig.loginData();
    if (user?.UserRole === 'Parent' && !CommonConfig.isEmpty(user?.SelectedChildData)) {
      return user?.SelectedChildData?._id;
    }
    return user?._id;
  };

  const arrayMove = (arr, from, to) => {
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const flattenMenuOrder = (menus) => {
    const orders = [];
    const walk = (items) => {
      (items || []).forEach((item, idx) => {
        if (item?.id) orders.push({ MenuId: String(item.id), Sequence: idx });
        if (item?.items?.length) walk(item.items);
      });
    };
    walk(menus);
    return orders;
  };

  const saveMenuOrder = async (menus) => {
    try {
      const userId = getMenuUserId();
      if (!userId) return;
      const Orders = flattenMenuOrder(menus);
      const res = await api.post(APIConstant.path.saveUserMenuOrder, { UserId: userId, Orders });
      if (res?.success) {
        Toast.success({ message: res?.message || 'Menu order updated' });
      }
    } catch (err) {
      console.error('Failed to save menu order:', err);
    }
  };

  const handleSidebarDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.index === destination.index && source.droppableId === destination.droppableId) return;

    if (type === 'ROOT') {
      const next = arrayMove(sidebarMenus, source.index, destination.index);
      setSidebarMenus(next);
      saveMenuOrder(next);
    } else if (type?.startsWith('CHILD::')) {
      const parentId = type.slice('CHILD::'.length);
      const next = sidebarMenus.map((parent) => {
        if (String(parent.id) === parentId && parent.items) {
          return { ...parent, items: arrayMove(parent.items, source.index, destination.index) };
        }
        return parent;
      });
      setSidebarMenus(next);
      saveMenuOrder(next);
    }
  };

  const handleYearChange = async (e) => {
    setSelectedYear(e.value);
    const loginData = CommonConfig.loginData();
    if (loginData?.SelectedAcademicYearList && loginData?.SelectedChildData?._id) {
      loginData.SelectedAcademicYearList[loginData.SelectedChildData._id] = e.value;
    }else{
      loginData.SelectedAcademicYearList = { ...(loginData?.SelectedAcademicYearList || {}), [loginData?.SelectedChildData?._id]: e.value };
    }
    CommonConfig.setCookie('selectedAcademicYear', e.value);
    localStorage.setItem(
      'ERPLoginData',
      JSON.stringify(loginData)
    );
  }

  const markNotificationAsReadAPI = async (notificationId) => {
    try {
      const loginData = CommonConfig.loginData();

      const payload = {
        UserId: loginData?._id,
        NotificationId: notificationId,
      };

      const res = await api.post(APIConstant.path.MarkNotificationAsRead, payload);
      if (res.success) {
        await GetNotificationList(securityUserID, selectedYear, notificationType || "ALL", 1, limit, false, '');
      } else {
        Toast.error({ message: res.message || "Failed to mark as read" });
      }
    } catch (err) {
      catchResponse(err);
    }
  };

  const deleteNotificationAPI = async (notificationId) => {
    try {
      const loginData = CommonConfig.loginData();

      const payload = {
        UserId: loginData?._id,
        NotificationId: notificationId,
      };

      const res = await api.post(APIConstant.path.DeleteNotification, payload);

      if (res.success) {
        Toast.success({message: res?.message});
        await GetNotificationList(securityUserID, selectedYear, notificationType || "ALL", 1, limit, false, '');
      } else {
        Toast.error({ message: res.message || "Failed to delete notification" });
      }
    } catch (err) {
      catchResponse(err);
    }
  };

  const markAllNotificationsAsReadAPI = async () => {
    try {
      const loginData = CommonConfig.loginData();

      const payload = {
        UserId: loginData?._id,
        ChildId:
          loginData?.UserRole === "Parent"
            ? loginData?.SelectedChildData?._id
            : null,
      };

      const res = await api.post(APIConstant.path.MarkAllNotificationsAsRead, payload);
      if (res.success) {
        Toast.success({ message: res?.message });
        await GetNotificationList(securityUserID, selectedYear, notificationType || "ALL", 1, limit, false, '');
      } else {
        Toast.error({ message: res.message || "Failed to mark all as read" });
      }
    } catch (err) {
      console.log('erriririr: ', err);
      
      catchResponse(err);
    }
  };

  const loadMoreNotifications = () => {
    const nextPage = page + 1;

    if (notifications.length >= total) return;

    setPage(nextPage);
    GetNotificationList(securityUserID, selectedYear, notificationType || "ALL", nextPage, limit, true, '');
  };

  const handleNotificationClick = (notification) => {
    if (notification?.redirectUrl) {
      navigate(notification?.redirectUrl, {
        state: {
          examName: notification?.data?.examName
        }
      });
    }
  };

  const bell = !canViewNotif ? null : (
    <div
      onMouseDown={handleMouseDown}
      className={`notification-bell-wrapper ${isDragging ? "dragging" : ""}`}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <button
        className="notification-bell-button e-h-btn"
        onClick={(e) => {
          e.stopPropagation();
          setShowNotifications((prev) => !prev);
        }}
      >
        <i className="pi pi-bell bell-icon"></i>
      </button>

      <div className="notification-bell-badge">
        <span className="notification-bell-badge-ping"></span>
        <span className="notification-bell-badge-count">
          {unreadCount}
        </span>
      </div>

      {showNotifications && (
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: `clamp(12px, ${position.y}px, calc(100vh - 612px))`,
            left: `clamp(12px, ${position.x + 48}px, calc(100vw - 432px))`,
            width: '420px',
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'min(600px, calc(100vh - 24px))',
            background: '#ffffff',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 0 1px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 10000,
            border: '1px solid #e5e7eb'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '1.25rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="pi pi-bell" style={{
                fontSize: '1.25rem',
                color: '#14452f'
              }}></i>
              <h3 style={{
                margin: 0,
                fontSize: '1rem',
                fontWeight: '700',
                color: '#1c515e'
              }}>
                Notifications
              </h3>
            </div>

            <div style={{
              display: 'flex',
              gap: '0.5rem'
            }}>
              <button
                title="Mark all as read"
                onClick={markAllNotificationsAsReadAPI}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontSize: '1rem',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f0f4ff';
                  e.target.style.color = '#5b6ddf';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="pi pi-check-square"></i>
              </button>

              <button
                title="Close"
                onClick={() => setShowNotifications(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontSize: '1rem',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#fef2f2';
                  e.target.style.color = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#64748b';
                }}
              >
                <i className="pi pi-times"></i>
              </button>
            </div>
          </div>

          {/* Filter */}
          <div style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0
          }}>
            <Dropdown
              value={notificationType}
              options={notificationTypeOptions}
              onChange={(e) => {
                setNotificationType(e.value);
                setPage(1);
                GetNotificationList(securityUserID, selectedYear, e.value, 1, limit, false, '');
              }}
              placeholder="Filter by type"
              style={{
                width: '100%',
                fontSize: '0.875rem'
              }}
              appendTo={document.body}
            />
          </div>

          {/* Notifications List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {filteredNotifications.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '3rem 1.25rem',
                textAlign: 'center',
                color: '#78849e'
              }}>
                <i className="pi pi-inbox" style={{
                  fontSize: '2.5rem',
                  marginBottom: '0.75rem',
                  opacity: 0.5
                }}></i>
                <p style={{
                  margin: '0.5rem 0',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  No notifications found
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: '#a0aec0'
                }}>
                  You're all caught up!
                </p>
              </div>
            ) : (
              <>
                {Object.entries(groupedNotifications).map(([group, list]) => {
                  if (!list.length) return null;

                  return (
                    <div key={group}>
                      {/* Group Title */}
                      <div style={{
                        padding: '0.75rem 1.25rem',
                        background: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}>
                        {group}
                      </div>

                      {/* Notification Items */}
                      {list.map((n) => (
                        <div
                          key={n._id}
                          onClick={() => handleNotificationClick(n)}
                          style={{
                            padding: '1rem 1.25rem',
                            borderBottom: '1px solid #e5e7eb',
                            background: n.isRead ? '#ffffff' : '#f0f4ff',
                            cursor: n.redirectUrl ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            borderLeft: n.isRead ? '3px solid transparent' : '3px solid #5b6ddf',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '0.75rem'
                          }}
                          onMouseEnter={(e) => {
                            if (n.redirectUrl) {
                              e.currentTarget.style.background = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = n.isRead ? '#ffffff' : '#f0f4ff';
                          }}
                        >
                          {/* Content */}
                          <div style={{
                            flex: 1,
                            minWidth: 0
                          }}>
                            <div style={{
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              color: '#1c515e',
                              marginBottom: '0.25rem',
                              lineHeight: '1.3',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {n.title}
                            </div>
                            <div style={{
                              fontSize: '0.8rem',
                              color: '#64748b',
                              marginBottom: '0.5rem',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}>
                              {n.message}
                            </div>
                            {Array.isArray(n.data?.attachments) && n.data.attachments.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.5rem' }}>
                                {n.data.attachments.map((file, i) => {
                                  const url = `${apiBase}/Upload/Announcement/${file}?token=${CommonConfig.fileToken()}`;
                                  const downloadUrl = `${apiBase}/Upload/Announcement/download/${file}?token=${CommonConfig.fileToken()}`;
                                  const name = String(file).replace(/^\d+-/, '');
                                  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file);
                                  return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={name}>
                                        {isImage
                                          ? <img src={url} alt={name} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e5e7eb' }} />
                                          : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: '#2f6fed', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '3px 6px' }}><i className="pi pi-file"></i>{name}</span>}
                                      </a>
                                      <a href={downloadUrl} onClick={(e) => e.stopPropagation()} title={`Download ${name}`} style={{ color: '#5b6ddf', padding: '2px' }}>
                                        <i className="pi pi-download" style={{ fontSize: '0.8rem' }}></i>
                                      </a>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#a0aec0',
                              fontWeight: '500'
                            }}>
                              {formatTimeAgo(n.createdAt)}
                            </div>
                          </div>

                          {/* Actions */}
                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            flexShrink: 0
                          }}>
                            {!n.isRead && (
                              <button
                                title="Mark as read"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markNotificationAsReadAPI(n._id);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: '#5b6ddf',
                                  fontSize: '0.875rem',
                                  padding: '0.4rem 0.5rem',
                                  borderRadius: '0.3rem',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#f0f4ff';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                }}
                              >
                                <i className="pi pi-check"></i>
                              </button>
                            )}

                            {canDeleteNotif && (
                            <button
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotificationAPI(n._id);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#dc2626',
                                fontSize: '0.875rem',
                                padding: '0.4rem 0.5rem',
                                borderRadius: '0.3rem',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#fef2f2';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              <i className="pi pi-trash"></i>
                            </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Load More */}
          {notifications.length < total && (
            <div style={{
              padding: '1rem 1.25rem',
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center',
              flexShrink: 0
            }}>
              <button
                onClick={loadMoreNotifications}
                style={{
                  background: '#f0f4ff',
                  border: '1px solid #5b6ddf',
                  color: '#5b6ddf',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#5b6ddf';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f0f4ff';
                  e.target.style.color = '#5b6ddf';
                }}
              >
                <i className="pi pi-chevron-down" style={{ marginRight: '0.5rem' }}></i>
                Load More
              </button>
            </div>
          )}

          {/* View All Link */}
          <div style={{
            padding: '0.75rem 1.25rem',
            borderTop: '1px solid #e5e7eb',
            textAlign: 'center',
            flexShrink: 0
          }}>
            <button
              onClick={() => { setShowNotifications(false); navigate('/notifications'); }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#5b6ddf',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0.25rem',
              }}
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const handleSidebarLogout = async () => {
    try {
      const loginData = CommonConfig.loginData();
      await CommonConfig.handleLogout(loginData?._id, navigate);
    } catch (err) {
      catchResponse(err);
    }
  };

  const [showSwitcher, setShowSwitcher] = useState(false);
  const [switchSchools, setSwitchSchools] = useState([]);
  const [pwSchool, setPwSchool] = useState(null);
  const [switchPassword, setSwitchPassword] = useState('');

  const getSwitchRecaptchaToken = () => new Promise((resolve, reject) => {
    if (!window.grecaptcha) return reject(new Error('reCAPTCHA not loaded'));
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.REACT_APP_GOOGLE_SITE_KEY, { action: 'login' })
        .then((t) => (t ? resolve(t) : reject(new Error('Failed to get reCAPTCHA token'))))
        .catch(reject);
    });
  });

  const openSwitcher = () => {
    setSwitchSchools(AccountStore.getDiscoveredSchools());
    setPwSchool(null);
    setSwitchPassword('');
    setShowSwitcher(true);
  };

  const doSwitchSchool = (school) => {
    const activeId = AccountStore.getActiveUserId();
    if (school.userId === activeId) { setShowSwitcher(false); return; }
    if (AccountStore.hasAccount(school.userId)) {
      AccountStore.activate(school.userId);
      window.location.reload();
    } else {
      setPwSchool(school);
      setSwitchPassword('');
    }
  };

  const submitSwitchPassword = async () => {
    if (!switchPassword) { Toast.error({ message: 'Password is required' }); return; }
    Loader.show();
    try {
      const token = await getSwitchRecaptchaToken();
      const identifier = pwSchool?.phone || pwSchool?.email || '';
      const res = await api.post(APIConstant.path.login, {
        Phone: identifier,
        Password: switchPassword,
        DeviceType: 'Web',
        isAlreadyLogin: false,
        IsOTPLogin: false,
        UserType: 'User',
        RecaptchaToken: token,
        UserId: pwSchool?.userId,
      });
      if (res.success && res?.data && !CommonConfig.isEmpty(res?.data?.Token)) {
        const userData = res.data;
        localStorage.setItem('ERPLoginData', JSON.stringify(userData));
        CommonConfig.setCookie('selectedAcademicYear', userData?.SelectedAcademicYear ?? '');
        CommonConfig.setCookie('access_token', userData?.Token?.toString());
        CommonConfig.setCookie('refresh_token', userData?.RefreshToken?.toString());
        CommonConfig.setCookie('refreshValue', userData?.RefreshValue?.toString());
        CommonConfig.setCookie('loggedIn', true.toString());
        AccountStore.saveActiveAccount(userData);
        window.location.reload();
      } else {
        Toast.error({ message: res.message || 'Login failed' });
      }
    } catch (err) {
      Toast.error({ message: err.message || 'Switch failed' });
    } finally {
      Loader.hide();
    }
  };

  const activeAccountId = AccountStore.getActiveUserId();
  const hasMultipleSchools = AccountStore.getDiscoveredSchools().length > 1;

  return (
    <div ref={sidebarRef} className={`sidebar themed-sidebar ${isExpanded ? '' : 'collapse'}`}>
      <div>
        <div className='et-brand'>
          <div className='et-badge'><img src={EduTattvaMono} alt='Edutattva' /></div>
          {isExpanded ? (
            <div className='et-wordmark'>
              <div className='et-name'><span className='et-edu'>Edu</span><span className='et-tattva'>Tattva</span></div>
              <div className='et-tag'>Smart Campus OS</div>
            </div>
          ) : null}
        </div>
        <Header isExpanded={isExpanded} />
        {isExpanded ? (
          <div className='et-search'>
            <i className='pi pi-search' />
            <input
              type='text'
              placeholder='Search menu…'
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
            />
          </div>
        ) : null}
        {userRole === 'Parent' && selectedChildName && (
          <div className="selected-child-container">
            <div>
              <div className="child-avatar">
                {selectedChildProfile ?
                  (
                    <img
                      src={`${apiBase}/UserProfilePic/${selectedChildProfile}`}
                    />) :
                  <span className="child-initial">
                    {selectedChildName?.charAt(0)?.toUpperCase()}
                  </span>
                }
              </div>
              <div className="child-name">
                {selectedChildName}
              </div>
            </div>
            <Dropdown
              key="selectedYear"
              className="child-year-dropdown"
              name="selectedYear"
              inputId="selectedYear"
              value={selectedYear}
              options={childAcademicDataList || []}
              onChange={handleYearChange}
              optionLabel="label"
              optionValue="_id"
              placeholder="Select Academic Year"
              filter
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        {filteredMenus?.length > 0 ? (
          isExpanded && !searchText ? (
            <DragDropContext onDragEnd={handleSidebarDragEnd}>
              <Droppable droppableId="sidebar-root" type="ROOT">
                {(provided) => (
                  <nav ref={provided.innerRef} {...provided.droppableProps}>
                    {filteredMenus.map((x, idx) => {
                      const isOpen = x.isActive;
                      const itemKey = String(x.id ?? x.path ?? idx);
                      return (
                        <Fragment key={itemKey}>
                          <Draggable draggableId={`root-${itemKey}`} index={idx}>
                            {(p, snapshot) => (
                              <div
                                className={`sidebar-menuitem ${snapshot.isDragging ? 'is-dragging' : ''}`}
                                ref={p.innerRef}
                                {...p.draggableProps}
                              >
                                <a href={x?.path}
                                  {...p.dragHandleProps}
                                  className={(window.location.pathname == x.path ||
                                    (x.items?.map(i => i.path)?.includes(window.location.pathname)))
                                    ? "active" : ""}
                                  onClick={(e) => {
                                    if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                      e.preventDefault();
                                      if (x.items) {
                                        toggleMenuExpanded(x);
                                      } else {
                                        navigate(x.path);
                                      }
                                    }
                                  }}>
                                  <i className="pi pi-bars et-drag-handle" />
                                  <i className={x?.icon} />
                                  {x.label}
                                  {x.items ?
                                    <i className={`t-menu-toggle-icon pi ${isOpen ? 'pi-angle-up' : 'pi-angle-down'}`} /> : null}
                                </a>
                              </div>
                            )}
                          </Draggable>

                          {isOpen && x.items ? (
                            <Droppable droppableId={`child-${itemKey}`} type={`CHILD::${itemKey}`}>
                              {(childProvided) => (
                                <div className="header-submenu" ref={childProvided.innerRef} {...childProvided.droppableProps}>
                                  {x.items.map((child, indx) => {
                                    const childKey = String(child.id ?? child.path ?? indx);
                                    return (
                                      <Draggable key={childKey} draggableId={`child-${itemKey}-${childKey}`} index={indx}>
                                        {(cp, csnap) => (
                                          <a
                                            ref={cp.innerRef}
                                            {...cp.draggableProps}
                                            {...cp.dragHandleProps}
                                            className={`${window.location.pathname == child.path ? "submenu-item active" : "submenu-item"} ${csnap.isDragging ? 'is-dragging' : ''}`}
                                            href={child?.path}
                                            onClick={(e) => {
                                              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                                e.preventDefault();
                                                navigate(child.path);
                                              }
                                            }}
                                          >
                                            <i className="pi pi-bars et-drag-handle" />
                                            <i className={child?.icon} />
                                            <span className="submenu-label">{child.label}</span>
                                          </a>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {childProvided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          ) : null}
                        </Fragment>
                      );
                    })}
                    {provided.placeholder}
                  </nav>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <nav>
              {filteredMenus.map((x, idx) => {
                const isOpen = searchText ? true : x.isActive;
                return (
                  <div className='sidebar-menuitem' key={idx}>
                    <a href={x?.path}
                      className={(window.location.pathname == x.path ||
                        (x.items?.map(i => i.path)?.includes(window.location.pathname)))
                        ? "active" : ""}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          if (x.items) {
                            toggleMenuExpanded(x);
                          } else {
                            navigate(x.path);
                          }
                        }
                      }}>
                      <i className={x?.icon} />
                      {isExpanded ? x.label : null}
                      {x.items && isExpanded ?
                        <i className={`t-menu-toggle-icon pi ${isOpen ? 'pi-angle-up' : 'pi-angle-down'}`} /> : null}
                    </a>

                    {isExpanded && isOpen ? (
                      <div className="header-submenu">
                        {x.items?.map((child, indx) => (
                          <a
                            key={indx}
                            className={window.location.pathname == child.path ? "submenu-item active" : "submenu-item"}
                            href={child?.path}
                            onClick={(e) => {
                              if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                e.preventDefault();
                                navigate(child.path);
                              }
                            }}
                          >
                            <i className={child?.icon} />
                            <span className="submenu-label">{child.label}</span>
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </nav>
          )
        ) : searchText ? (
          <p>No menus match “{menuSearch.trim()}”</p>
        ) : (
          <p>No menus available</p>
        )}
      </div>
      <div className='et-bottom'>
        <div className='et-footer'>
          <img src={portlinksLogo} alt='' width={28} style={{ filter: logoWhite ? 'brightness(0) invert(1)' : 'none' }} />
          {isExpanded ? (
            <div className='et-footer-text'>
              <span>© 2026 · All rights reserved by</span>
              <a href='https://www.portlinksindia.com/' target='blank'>PORTLINKS INDIA</a>
            </div>
          ) : null}
        </div>
      </div>
      <div className='et-side-controls'>
        <button className='et-cbtn et-cbtn--collapse' title={isExpanded ? 'Collapse' : 'Expand'} onClick={() => setIsExpanded(!isExpanded)}><i className={`pi ${isExpanded ? 'pi-angle-double-left' : 'pi-angle-double-right'}`} /></button>
        {canViewNotif && (
        <button
          className='et-cbtn et-cbtn--bell'
          title='Notifications'
          onMouseDown={handleMouseDown}
          style={{ position: 'fixed', left: position.x, top: position.y, zIndex: 10001 }}
          onClick={() => setShowNotifications((s) => !s)}
        ><i className='pi pi-bell' /></button>
        )}
        <button className='et-cbtn et-cbtn--theme' title='Select Theme' onClick={(e) => themePickerRef.current?.toggle(e)}><i className='pi pi-palette' /></button>
        {hasMultipleSchools && (
          <button className='et-cbtn et-cbtn--switch' title='Switch School' onClick={openSwitcher}><i className='pi pi-sync' /></button>
        )}
        <button className='et-cbtn et-cbtn--danger et-cbtn--logout' title='Logout' onClick={handleSidebarLogout}><i className='pi pi-sign-out' /></button>
      </div>

      <Dialog
        visible={showSwitcher}
        onHide={() => setShowSwitcher(false)}
        header={
          <div className="dialog-header">
            <i className="pi pi-building" />
            <span className="dialog-title">{pwSchool ? 'Sign in to school' : 'Switch School'}</span>
          </div>
        }
        draggable={false}
        dismissableMask
        position="center"
        className="confirmation-dialog"
        style={{ width: '440px' }}
      >
        {!pwSchool ? (
          <div className='ls-school-list'>
            {switchSchools.map((s) => {
              const isActive = s.userId === activeAccountId;
              const isLoggedIn = AccountStore.hasAccount(s.userId);
              return (
                <button
                  type='button'
                  key={s.userId}
                  className={`ls-school-item ${isActive ? 'ls-school-item--active' : ''}`}
                  disabled={isActive}
                  onClick={() => doSwitchSchool(s)}
                >
                  <span className='ls-school-ic'><i className='pi pi-building' /></span>
                  <span className='ls-school-meta'>
                    <span className='ls-school-name'>{s.schoolName || 'School'}</span>
                    <span className='ls-school-sub'>
                      {isActive ? 'Current' : isLoggedIn ? 'Logged in — tap to switch' : 'Sign in required'}
                    </span>
                  </span>
                  {isActive ? <i className='pi pi-check' /> : <i className='pi pi-angle-right' />}
                </button>
              );
            })}
          </div>
        ) : (
          <div className='dialog-content'>
            <div className='ls-selected-school'>
              <i className='pi pi-building' /> {pwSchool.schoolName || 'School'}
            </div>
            <div className='ls-field-group'>
              <label htmlFor='switchPassword'>Password</label>
              <div className='ls-field'>
                <i className='pi pi-lock' />
                <InputText
                  id='switchPassword'
                  type='password'
                  placeholder='Enter your password'
                  maxLength={20}
                  value={switchPassword}
                  onChange={(e) => setSwitchPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitSwitchPassword(); }}
                />
              </div>
            </div>
            <div className="dialog-footer">
              <Button label="Back" icon="pi pi-arrow-left" className="btn-secondary" outlined onClick={() => setPwSchool(null)} />
              <Button label="Sign In" icon="pi pi-check" className="btn-primary" onClick={submitSwitchPassword} />
            </div>
          </div>
        )}
      </Dialog>

      <ThemePicker ref={themePickerRef} />
      {createPortal(bell, document.body)}
    </div>
  );
};

export default Sidebar;


